<template>
  <v-navigation-drawer v-model="isActive" width="272" :temporary="mobile"
    class="!tw-fixed !tw-top-0 tw-min-h-screen !tw-bg-[#17141f] !tw-border-e-0"
    :absolute="false" :location="$root.$i18n.locale == 'ar' ? 'right' : 'left'">

    <div class="tw-flex tw-flex-col tw-h-full tw-min-h-screen tw-bg-[#17141f] tw-text-gray-300">

      <!-- Header: nav background image faded into the sidebar, with logo + user info on top -->
      <div class="tw-relative tw-overflow-hidden tw-h-[200px] tw-border-b tw-border-solid tw-border-white/10">
        <!-- Background image -->
        <div class="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center" style="background-image: url('/assets/images/background-nav.jpg')"></div>
        <!-- Gradient fade into the sidebar color (lighter at the top) -->
        <div class="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-[#17141f]/10 tw-via-[#17141f]/50 tw-to-[#17141f]"></div>

        <div class="tw-relative tw-h-full tw-flex tw-flex-col tw-justify-between tw-p-4">
          <!-- Logo (hidden) -->
          <div class="tw-invisible tw-justify-end">
            <AppLogo dark />
          </div>

          <!-- User info (borderless) -->
          <div v-if="authStore.user" class="tw-flex tw-items-center tw-gap-3">
            <div class="tw-relative tw-shrink-0">
              <div class="tw-h-11 tw-w-11 tw-rounded-full tw-bg-gradient-to-tr tw-from-primary-500 tw-to-primary-700 tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-shadow-primary-900/40">
                <span class="tw-text-white tw-font-bold tw-text-sm tw-uppercase">{{ initials }}</span>
              </div>
              <!-- Online dot -->
              <span class="tw-absolute tw-bottom-0 tw-right-0 tw-h-3 tw-w-3 tw-rounded-full tw-bg-emerald-400 tw-border-2 tw-border-solid tw-border-[#17141f]"></span>
            </div>
            <div class="tw-min-w-0">
              <p class="tw-text-sm tw-font-semibold tw-text-white tw-truncate">{{ authStore.user.name }}</p>
              <p class="tw-text-[11px] tw-text-primary-200 tw-capitalize tw-truncate">{{ roleLabel }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Menu -->
      <div class="tw-flex-1 tw-overflow-y-auto tw-p-3 tw-space-y-1">
        <p class="tw-text-[10px] tw-px-2 tw-font-bold tw-mb-1 tw-text-gray-500 tw-uppercase tw-tracking-wider">
          {{ $t('dashboard.sidebar.menu') }}
        </p>

        <router-link v-for="link in links" :key="link.id" :to="{ name: link.to }" @click="closeOnMobile"
          class="tw-group tw-flex tw-flex-row tw-items-center tw-gap-3 tw-rounded-lg tw-px-2 tw-py-2 tw-duration-200 tw-no-underline"
          :class="isActiveLink(link)
            ? 'tw-bg-gradient-to-r tw-from-primary-500 tw-to-primary-600 tw-shadow-lg tw-shadow-primary-900/40'
            : 'hover:tw-bg-white/[0.06]'">
          <div class="tw-p-1 tw-rounded-md tw-border tw-border-solid tw-duration-200"
            :class="isActiveLink(link) ? 'tw-bg-white/20 tw-border-white/20' : 'tw-bg-white/[0.04] tw-border-white/10 group-hover:tw-border-white/20'">
            <icon class="tw-text-lg"
              :class="isActiveLink(link) ? 'tw-text-white' : 'tw-text-gray-400 group-hover:tw-text-white'"
              :icon="isActiveLink(link) ? link.icon.enabled : link.icon.disabled" />
          </div>
          <span class="tw-font-semibold tw-text-sm"
            :class="isActiveLink(link) ? 'tw-text-white' : 'tw-text-gray-300 group-hover:tw-text-white'">
            {{ $t(link.name) }}
          </span>

          <span v-if="badgeFor(link) > 0"
            class="tw-ml-auto tw-min-w-[20px] tw-h-[20px] tw-px-1.5 tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-bold"
            :class="isActiveLink(link) ? 'tw-bg-white tw-text-primary-600' : 'tw-bg-primary-500 tw-text-white'">
            {{ badgeFor(link) > 99 ? '99+' : badgeFor(link) }}
          </span>
        </router-link>
      </div>

      <!-- Logout -->
      <div class="tw-p-3 tw-border-t tw-border-solid tw-border-white/10">
        <button :disabled="isLoggingOut" @click="handleLogout"
          class="tw-w-full tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-px-2 tw-py-2 tw-duration-200 tw-text-rose-300 hover:tw-bg-rose-500/10 hover:tw-text-rose-200 disabled:tw-opacity-60">
          <div class="tw-p-1 tw-rounded-md tw-border tw-border-solid tw-border-white/10 tw-bg-white/[0.04]">
            <icon class="tw-text-lg" :icon="isLoggingOut ? 'line-md:loading-alt-loop' : 'solar:logout-3-bold-duotone'" />
          </div>
          <span class="tw-font-semibold tw-text-sm">{{ $t('buttons.logout') }}</span>
        </button>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script setup>
import allLinks from '@/data/links.json'
import AppLogo from '@/components/AppLogo.vue';
import { useAuthStore, usePortalStore } from '@/stores';
import { defineProps, defineEmits, computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { logout } from '@/api/auth';

const authStore = useAuthStore();
const portalStore = usePortalStore();
const route = useRoute();
const router = useRouter();
const { mobile } = useDisplay();
const isLoggingOut = ref(false);

// On mobile the drawer overlays content, so close it after navigating.
const closeOnMobile = () => { if (mobile.value) isActive.value = false; };

const handleLogout = async () => {
  isLoggingOut.value = true;
  // Capture role before clearing so clients return to the portal login.
  const wasClient = authStore.user?.role === 'client';
  await logout();
  authStore.logout();
  router.push({ name: wasClient ? 'auth.client-login' : 'auth.login' });
  isLoggingOut.value = false;
};
const emit = defineEmits(['update:drawer'])
const props = defineProps({
  drawer: {
    required: true
  }
})

const links = computed(() => {
  const role = authStore.user?.role;
  return (allLinks[role] || allLinks.default || []).filter(l => l.active);
})

// Highlight the link for its own route and any child routes (e.g. detail pages).
const isActiveLink = (link) => route.name === link.to || String(route.name || '').startsWith(link.to + '.');

const badgeFor = (link) => {
  if (link.to === 'portal.notifications') return portalStore.unreadNotifications;
  if (link.to === 'portal.tickets') return portalStore.unreadTickets;
  if (link.to === 'admin.tickets') return portalStore.openTickets;
  return 0;
};

const roleLabel = computed(() => (authStore.user?.role || '').replace(/_/g, ' '));

const initials = computed(() => {
  const name = authStore.user?.name || '';
  return name.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('') || 'U';
});

const isActive = computed({
  get: () => props.drawer,
  set: (v) => emit('update:drawer', v)
})
</script>

<style scoped>
:deep(.v-navigation-drawer__content) {
  background: #17141f;
}
</style>
